import React, { useCallback, useState } from "react";
import * as Scrivito from "scrivito";
import { Table } from "reactstrap";
import { show } from "@ebay/nice-modal-react";
import { ProjectListTableRowSkeleton } from "objs/_ConfiguratorObjs/ProjectList/SubComponents/ProjectListTable/ProjectListTableRowSkeleton";
import { Checkbox, SortButton, UpdateProjectStatusModal } from "components";

import { ConfirmationModal } from "components/_Modals/ConfirmationModal";
import { MultipleProjectsDeleteMut } from "api/mutations";
import { useTranslate } from "hooks";
import { useProjectDataStore } from "stores/projectStore";
import { useAppBaseContext } from "providers";
import { ProjectListTableRow } from "./ProjectListTableRow";

export const ProjectListTable = Scrivito.connect(
  ({
    error,
    fetching,
    isOrderDescending,
    projects,
    projectItemObj,
    setIsOrderDescending,
    skeletonCount,
    statusColors,
    refetchProjectList,
    projectsToDelete,
    setProjectsToDelete,
    isSelectAll,
    setIsSelectAll,
    resetUrlParams,
    setCurrentPageNo,
  }) => {
    const translate = useTranslate();
    const { lang: language } = useAppBaseContext();
    const [project, setProject] = useState({});
    const projectId = useProjectDataStore((state) => state.projectId);
    const toggleProjectMenu = useCallback((projectData) => {
      if (projectData.id) {
        setProject(projectData);
      }
    }, []);
    const [spinnerId, setSpinnerId] = useState();

    const toggleSelectAll = () => {
      if (!isSelectAll) {
        setProjectsToDelete(projects.map((proj) => proj.id));
      } else {
        setProjectsToDelete([]);
      }
      setIsSelectAll(!isSelectAll);
    };

    const handleCheckboxClick = (itemId) => {
      const updatedCheckboxes = projectsToDelete.includes(itemId)
        ? projectsToDelete.filter((id) => id !== itemId)
        : [...projectsToDelete, itemId];
      setProjectsToDelete(updatedCheckboxes);
      setIsSelectAll(updatedCheckboxes.length === projects.length);
    };

    const toggleModalStatus = useCallback(() => {
      show(UpdateProjectStatusModal, { project }).then(() => {
        setSpinnerId(project.id);
        setTimeout(async () => {
          refetchProjectList({
            requestPolicy: "network-only",
          });
          setSpinnerId(null);
        }, 2000);
      });
      toggleProjectMenu({});
    }, [project, refetchProjectList, toggleProjectMenu]);

    const lastModificationOrderToggle = useCallback(() => {
      setIsOrderDescending((prev) => !prev);
    }, [setIsOrderDescending]);

    const toggleDeleteModal = useCallback(() => {
      toggleProjectMenu({});
      show(ConfirmationModal, {
        submitBtnText: translate("PROJECT_MODAL_DELETE_TEXT"),
        title: translate("PROJECT_MODAL_DELETE_TEXT"),
        alertText: translate(
          "PROJECT_MODAL_DELETE_WARNING_TEXT_WITH_TITLE",
          project.title,
        ),
        mutation: MultipleProjectsDeleteMut,
        params: { input: { ids: [project?.id], languageCode: language } },
      }).then(() => {
        setSpinnerId(project?.id);
        setTimeout(() => {
          refetchProjectList({
            requestPolicy: "network-only",
          });
          if (projectId && project?.id === projectId) {
            resetUrlParams();
          }
          setSpinnerId(null);
        }, 2000);
      });
    }, [
      toggleProjectMenu,
      translate,
      project.title,
      project?.id,
      refetchProjectList,
      projectId,
      resetUrlParams,
      language,
    ]);

    if (error) {
      return (
        <div className="mb-6">
          <span>{translate("ERROR_OCCURED")}</span>
        </div>
      );
    }

    return (
      <>
        <Table className="mb-4">
          <thead>
            <tr>
              <th
                className="d-flex gap-3 w-100 justify-content-between"
                data-testid="PROJECT_LIST_PROJECT"
              >
                <div>
                  <Checkbox
                    onChange={toggleSelectAll}
                    checked={isSelectAll}
                    wrapperClassName="me-3"
                  />
                  {translate("PROJECT_LIST_PROJECT")}
                </div>
                {/* <SortButton disabled /> */}
              </th>
              <th>
                <div
                  className="d-flex justify-content-between position-relative"
                  data-testid="PROJECT_LIST_LAST_MODIFICATION"
                >
                  {translate("PROJECT_LIST_LAST_MODIFICATION")}
                  <SortButton
                    toggleFunc={lastModificationOrderToggle}
                    isOrderDescending={isOrderDescending}
                    setIsOrderDescending={setIsOrderDescending}
                    setCurrentPageNo={setCurrentPageNo}
                  />
                </div>
              </th>
              <th data-testid="PROJECT_SHARING_SETTINGS">
                {translate("PROJECT_SHARING_SETTINGS")}
              </th>
              <th colSpan="2" data-testid="PROJECT_LIST_STATUS">
                {translate("PROJECT_LIST_STATUS")}
              </th>
            </tr>
          </thead>
          <tbody>
            {fetching ? (
              <tr>
                {Array(skeletonCount)
                  .fill(null)
                  .map((_i, key) => (
                    <ProjectListTableRowSkeleton key={key} />
                  ))}
              </tr>
            ) : (
              <>
                {projects.length > 0 ? (
                  projects.map((proj) => (
                    <ProjectListTableRow
                      key={proj.id}
                      project={proj}
                      projectItemObj={projectItemObj}
                      statusColors={statusColors}
                      toggleProjectMenu={toggleProjectMenu}
                      toggleModalStatus={toggleModalStatus}
                      toggleDeleteModal={toggleDeleteModal}
                      handleCheckboxClick={handleCheckboxClick}
                      isChecked={projectsToDelete.includes(proj.id)}
                      refetchProjectList={refetchProjectList}
                      spinnerId={spinnerId}
                    />
                  ))
                ) : (
                  <tr>
                    <td
                      className="text-center"
                      colSpan="5"
                      data-testid="PROJECT_LIST_NO_DATA"
                    >
                      {translate("PROJECT_LIST_NO_DATA")}
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </Table>
      </>
    );
  },
);
